/**
 * Wizard personal data formik
 *
 * @author Fabio Dal Santo <fabio@gestionaleauto.com>
 */

/**
 * Main
 */
import React, { useEffect, useState } from 'react';
import HttpStatus from 'http-status-codes';
import { useTranslation } from 'react-i18next';
import { useFormik, setNestedObjectValues } from 'formik';
import { isValidPhoneNumber } from 'libphonenumber-js';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
/**
 * Material UI
 */
import { makeStyles } from '@mui/styles';
/**
 * Redux
 */
import { useSelector, useDispatch } from 'react-redux';
import {
  initUpdate,
  successUpdate,
  failUpdate,
  setStepValidation,
  wizardState,
} from '../../redux/wizardSlice';
import { updateLeadId, updatePersonalData, leadState } from '../../redux/leadSlice';
import { lookupsState } from '../../redux/lookupsSlice';
/**
 * Helpers
 */
import Api from '../../helpers/api';
import { PROVINCES } from '../../helpers/const';
/**
 * Components
 */
import FormSwitch from '../../components/FormSwitch';
import InputText from '../../components/form/InputText';
import InputSelect from '../../components/form/InputSelect';
import InputCheckbox from '../../components/form/InputCheckbox';
import { getToken } from '../../helpers/auth';

const useStyles = makeStyles((theme) => ({
  formSwitchWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 20,
    marginBottom: 20,
    alignItems: 'center',
    '& > div:first-child': {
      lineHeight: '32px',
      marginRight: 15,
      fontSize: 24,
      textAlign: 'left',
      fontWeight: 'bold',
    },
    [theme.breakpoints.up('md')]: {
      marginTop: 20,
      marginBottom: 20,
    },
  },
}));

/**
 * Personal data step
 * @returns {JSX.Element}
 */
function PersonalData({ showReceiveOffer, showTradeFlag, showFinancialFlag }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { actionRequired, validateForm, step } = useSelector(wizardState);
  const { configuration } = useSelector(lookupsState);
  const { leadId, personalData } = useSelector(leadState);

  const {
    checkMarketing, checkPrivacy, checkProfiling, marketingMandatory, privacyMandatory,
    profilingMandatory, marketingText, privacyText, profilingText,
  } = configuration.privacy || {};
  const {
    blogId, hasGl, vehicle, language, extraFields: paramsExtraFields,
  } = configuration;
  let extraFields = [];
  if (paramsExtraFields && paramsExtraFields.length) {
    extraFields = paramsExtraFields.filter((record) => record.step === step);
  }

  /**
   * Variables for reCAPTCHA v3
   */
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const siteDomain = window.location.hostname;
  const isSpecialDomain = siteDomain === 'www.autoserenissima30.it';

  /**
   * Formik initial values
   */
  const initialValues = {
    id: leadId,
    name: personalData.name || '',
    surname: personalData.surname || '',
    email: personalData.email || '',
    phone: personalData.phone || '',
    province: personalData.province || '',
    tradeIn: personalData.tradeIn === true || false,
    financing: personalData.financing === true || false,
    checkPrivacy,
    privacyMandatory: privacyMandatory === true || false,
    checkMarketing,
    marketingMandatory: marketingMandatory === true || false,
    checkProfiling,
    profilingMandatory: profilingMandatory === true || false,
    receiveOffer: personalData.receiveOffer === true || false,
  };
  if (extraFields.length) {
    for (let i = 0; i < extraFields.length; i += 1) {
      initialValues[extraFields[i].key] = personalData[extraFields[i].key] || '';
    }
  }

  initialValues.privacyCheck = personalData.privacyCheck === true || false;
  initialValues.marketingCheck = personalData.marketingCheck === true || false;
  initialValues.profilingCheck = personalData.profilingCheck === true || false;
  /**
   * Compile extra validation schema
   */
  const extraValidationSchema = () => {
    if (!extraFields.length) {
      return {};
    }
    const extraSchema = {};
    for (let i = 0; i < extraFields.length; i += 1) {
      if (extraFields[i].required) {
        extraSchema[extraFields[i].key] = Yup.string().required('Required field');
      }
    }
    return extraSchema;
  };
  /**
   * Formik validation schema
   */
  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: false,
    validateOnBlur: false,
    validateOnChange: true,
    initialValues,
    validationSchema: Yup.object().shape({
      name: Yup.string().min(1).max(149).required('Required field'),
      surname: Yup.string().min(1).max(149).required('Required field'),
      email: Yup.string().email('Invalid email address').required('Required email'),
      province: Yup.string().required('Required field'),
      phone: Yup.string().required('Required phone').test({
        name: 'phone',
        message: 'Invalid format',
        exclusive: false,
        test(val = '') {
          if (!val) {
            return true;
          }
          return !!isValidPhoneNumber(val, 'IT');
        },
      }),
      privacyCheck: Yup.boolean().when(['checkPrivacy', 'privacyMandatory'], {
        is: true,
        then: Yup.boolean().required('Required field').oneOf([true], 'Required field'),
      }),
      marketingCheck: Yup.boolean().when(['checkMarketing', 'marketingMandatory'], {
        is: true,
        then: Yup.boolean().required('Required field').oneOf([true], 'Required field'),
      }),
      profilingCheck: Yup.boolean().when(['checkProfiling', 'profilingMandatory'], {
        is: true,
        then: Yup.boolean().required('Required field').oneOf([true], 'Required field'),
      }),
      ...extraValidationSchema(),
    }),
    validate: () => {},
  });

  useEffect(() => {
    if (leadId > 0) {
      dispatch(setStepValidation(formik.isValid));
    } else {
      dispatch(setStepValidation(formik.dirty && formik.isValid));
    }
  }, [formik.isValid, formik.dirty, leadId]);
  /**
   * Touch fields with errors when validateForm trigger is dispatched
   */
  useEffect(() => {
    async function validate() {
      if (validateForm) {
        const validationErrors = await formik.validateForm();
        if (Object.keys(validationErrors).length > 0) {
          await formik.setTouched(setNestedObjectValues(validationErrors, true));
        }
      }
    }
    validate();
  }, [validateForm]);
  /**
   * Set token for Google recaptcha v3
   */
  useEffect(() => {
    if (isSpecialDomain) {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute('6LfINhwqAAAAAIRrSG4JktqWqdfRvEI70Hc94p1E', { action: 'quotation_form' }).then((token) => {
          setRecaptchaToken(token);
        });
      });
    }
  }, []);

  const submitForm = async () => {
    const { id } = formik.values;
    const formData = {
      id,
      name: formik.values.name,
      surname: formik.values.surname,
      email: formik.values.email,
      phone: formik.values.phone,
      province: formik.values.province,
      marketing: formik.values.marketingCheck,
      privacy: formik.values.privacyCheck,
      profiling: formik.values.profilingCheck,
      trade: formik.values.tradeIn,
      receive_offer: formik.values.receiveOffer,
      use_financial: formik.values.financing,
      language,
      has_gl: hasGl,
      blog_id: blogId,
      site_url: window.location.origin,
      page_url: window.location.pathname + window.location.search,
      stock_id: 0, // Quotation mode
      vehicle_data: {},
    };
    if (vehicle) {
      formData.stock_id = vehicle.id;
      formData.vehicle_data = {
        make: vehicle.make,
        model: vehicle.model,
        price: vehicle.price,
        internal_code: vehicle.internal_code,
        version: vehicle.version,
        mileage: vehicle.mileage,
        usage: vehicle.usage,
        body: vehicle.body,
        fuel: vehicle.fuel,
        color: vehicle.color,
        kwatt: vehicle.kwatt,
        gearbox: vehicle.gearbox,
        doors: vehicle.doors,
        url: vehicle.url || '',
      };
    }
    if (extraFields.length) {
      const tempExtraFields = [];
      for (let i = 0; i < extraFields.length; i += 1) {
        if (formik.values[extraFields[i].key]) {
          tempExtraFields.push({
            text: extraFields[i].mailText,
            value: formik.values[extraFields[i].key],
            step,
          });
        }
      }
      if (tempExtraFields.length) {
        formData.extra_fields = tempExtraFields;
      }
    }
    if (isSpecialDomain) {
      formData.recaptchaToken = recaptchaToken;
    }

    const config = {
      entryPoint: id > 0 ? `/lead/${id}` : '/lead',
      headers: id > 0 ? { 'ga-token': getToken(id) } : {},
      data: formData,
    };
    const executeApiCall = () => (id > 0 ? Api.patch(config) : Api.post(config));
    const res = await executeApiCall();

    if (res.status !== HttpStatus.OK) {
      dispatch(failUpdate());
      return;
    }

    if (res.data.success) {
      if (res.data && res.data.id) {
        await formik.setFieldValue('id', res.data.id);
        dispatch(updateLeadId(res.data.id));
      }
      dispatch(updatePersonalData(formik.values));
      dispatch(successUpdate());
    }
  };
  useEffect(() => {
    if (actionRequired) {
      dispatch(initUpdate());
      submitForm();
    }
  }, [actionRequired]);

  const renderPrivacyButton = (key, required, text) => {
    const fieldName = `${key}Check`;
    const capName = key.charAt(0).toUpperCase() + key.slice(1);

    return (
      <InputCheckbox
        formik={formik}
        className={`mdl-textfield mdl-js-textfield${formik.errors[fieldName] && (formik.touched[fieldName] || validateForm) ? ' is-invalid' : ''}`}
        name={fieldName}
        label={text}
        required={required}
        errorMessage={t('Please read and accept the ### Policy.').replace('###', capName)}
      />
    );
  };

  return (
    <div className="col-xs-12">
      <InputText
        formik={formik}
        name="name"
        label={`${t('Name')} *`}
        errorMessage={t('Please insert a name.')}
      />
      <InputText
        formik={formik}
        name="surname"
        label={`${t('Surname')} *`}
        errorMessage={t('Please insert a surname.')}
      />
      <InputText
        formik={formik}
        name="email"
        label={`${t('E-mail')} *`}
        errorMessage={t('Please insert a valid email address.')}
      />
      <InputText
        formik={formik}
        name="phone"
        label={`${t('Phone')} *`}
        errorMessage={t('Please insert a phone number.')}
      />
      <InputSelect
        formik={formik}
        items={PROVINCES.sort((a, b) => {
          if (a.text > b.text) {
            return 1;
          }
          return -1;
        })}
        selectClassName="ga-select"
        errorMessage={t('Please insert district')}
        name="province"
        label={`${t('Province')} *`}
        style={extraFields.length ? {} : { marginBottom: 0 }}
      />
      { extraFields.length > 0 && (
        extraFields.map((field) => {
          if (field.type === 'select') {
            return (
              <InputSelect
                key={field.key}
                formik={formik}
                items={field.values}
                selectClassName="ga-select"
                errorMessage={field.error}
                name={field.key}
                label={field.text}
              />
            );
          }
          return (
            <InputText
              key={field.key}
              formik={formik}
              name={field.key}
              label={field.text}
              errorMessage={field.error}
            />
          );
        })
      )}
      { showFinancialFlag && (
        <div className={classes.formSwitchWrapper}>
          <div>{t('Do you want to finance your purchase?')}</div>
          <FormSwitch
            checked={formik.values.financing}
            name="financing"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
      ) }
      {showTradeFlag && (
        <div className={classes.formSwitchWrapper}>
          <div>{t('I have a vehicle to trade in')}</div>
          <FormSwitch
            checked={formik.values.tradeIn}
            name="tradeIn"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
      )}
      { showReceiveOffer && (
        <div className={classes.formSwitchWrapper}>
          <div>{t('Do you want to receive an offer for your next car?')}</div>
          <FormSwitch
            checked={formik.values.receiveOffer}
            name="receiveOffer"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
      ) }
      <div className="ga-privacy-wrapper" style={{ textAlign: 'left', marginTop: 40 }}>
        {checkPrivacy
          && renderPrivacyButton(
            'privacy',
            privacyMandatory,
            privacyText,
          )}
        {checkMarketing
          && renderPrivacyButton(
            'marketing',
            marketingMandatory,
            marketingText,
          )}
        {checkProfiling
          && renderPrivacyButton(
            'profiling',
            profilingMandatory,
            profilingText,
          )}
      </div>
    </div>
  );
}
PersonalData.defaultProps = {
  showTradeFlag: true,
  showFinancialFlag: true,
  showReceiveOffer: false,
};
PersonalData.propTypes = {
  showTradeFlag: PropTypes.bool,
  showFinancialFlag: PropTypes.bool,
  showReceiveOffer: PropTypes.bool,
};
export default PersonalData;
